import {
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useDispatch } from 'react-redux';
import { useTheme } from '~/_shared/themes/theme.hooks';
import { isMarkerStyle } from '~/_shared/types/marker.types';
import { type SpreadsheetRowId } from '~/_shared/types/spreadsheetData/spreadsheetRow';
import { useIsMapInteractionActive } from '~/_shared/utils/hooks/useIsMapInteractionActive';
import { useGetMarkerEntityStyleFromMarkerSettings } from '~/_shared/utils/markers/useGetMarkerEntityStyleFromMarkerSettings.hook';
import { useDirectionsRouteZIndexes } from '~/map/zIndexes/useDirectionsRouteZIndexes.hook';
import { frontendStateDirectionsUpdateRouteData } from '~/store/frontendState/mapTools/directions/directions.actionCreators';
import { useDirectionsCanDragRoutesSelector } from '~/store/frontendState/mapTools/directions/directions.selectors';
import { type RouteUiData } from '~/store/frontendState/mapTools/directions/directions.state';
import { useMapSettingsDirectionsCollapseMarkersSelector } from '~/store/mapSettings/directions/mapSettingsDirections.selectors';
import { type MapSettingsRoute } from '~/store/mapSettings/directions/mapSettingsDirections.state';
import { useMapContext } from '../mapContext';
import { type MapMarkerEventCallback } from '../markers/useMarkerEvents/markerEventCallbacks.type';
import { useMarkerEventCallbacks } from '../markers/useMarkerEvents/useMarkerEventCallbacks';
import { registerMarkerTemplate } from '../markers/useMarkers/useMarkerStyles.hook';
import {
  getCollapsedDirectionsMarkerProps, type MapDirectionsManager,
} from './mapDirectionsManager';

const getMouseEventCallback = (callback: MapMarkerEventCallback) => (markerId: SpreadsheetRowId) =>
  callback([markerId], false, () => null);

type DirectionsRouteInstanceProps = Readonly<{
  manager: MapDirectionsManager;
  route: MapSettingsRoute;
  routeUiData: RouteUiData | undefined;
}>;

export const DirectionsRouteInstanceContainer: FC<DirectionsRouteInstanceProps> = props => {
  const { markerTemplateManager } = useMapContext();
  const canDragRoutes = useDirectionsCanDragRoutesSelector();
  const isMapInteractionsActive = useIsMapInteractionActive();
  const zIndexes = useDirectionsRouteZIndexes(props.route.waypoints);
  const dispatch = useDispatch();
  const theme = useTheme();
  const markerMouseEventCallbacks = useMarkerEventCallbacks();
  const getMarkerEntityStyleFromMarkerSettings = useGetMarkerEntityStyleFromMarkerSettings();
  const areMarkersCollapsed = useMapSettingsDirectionsCollapseMarkersSelector();

  const onRouteChange = useCallback((routeId: string, routes: ReadonlyArray<google.maps.DirectionsRoute>) => {
    dispatch(frontendStateDirectionsUpdateRouteData(routeId, routes));
  }, [dispatch]);
  const onRouteChangeRef = useRef(onRouteChange);

  const idDraggable = canDragRoutes && !isMapInteractionsActive;

  const mouseEventCallbacks = useMemo(() => ({
    onClick: getMouseEventCallback(markerMouseEventCallbacks.onClick),
    onMouseOut: getMouseEventCallback(markerMouseEventCallbacks.onMouseOut),
    onMouseOver: getMouseEventCallback(markerMouseEventCallbacks.onMouseOver),
  }), [markerMouseEventCallbacks.onClick, markerMouseEventCallbacks.onMouseOut, markerMouseEventCallbacks.onMouseOver]);

  const collapsedMarkerStyle = useMemo(() => {
    const markerSettings = getCollapsedDirectionsMarkerProps({ color: props.route.color });
    const markerEntityStyle = getMarkerEntityStyleFromMarkerSettings(markerSettings.markerSettings);
    if (isMarkerStyle(markerEntityStyle)) {
      return markerEntityStyle;
    }
    return null;
  }, [getMarkerEntityStyleFromMarkerSettings, props.route.color]);

  useEffect(() => {
    if (collapsedMarkerStyle) {
      registerMarkerTemplate(collapsedMarkerStyle, markerTemplateManager);
    }

    if (props.routeUiData) {
      props.manager.renderRoute(
        props.route,
        props.routeUiData,
        mouseEventCallbacks,
        zIndexes,
        idDraggable,
        onRouteChangeRef.current,
        theme,
        areMarkersCollapsed ? collapsedMarkerStyle || undefined : undefined,
      );
    }
  }, [props.route, props.routeUiData, props.manager, zIndexes, idDraggable, theme, mouseEventCallbacks, areMarkersCollapsed, getMarkerEntityStyleFromMarkerSettings, collapsedMarkerStyle, markerTemplateManager]);

  useEffect(() => () => props.manager.removeRoute(props.route.id), [props.route, props.manager]);

  return null;
};
